import { useMutation, QueryClient } from "@tanstack/react-query";
import { AxiosInstance } from "axios";
import { USER_API } from "config";
import { workspaceKeys } from "queries/workspace.keys";
import { normalizeId } from "utils";
import { handleToast } from "utils/toasts";
import {
  InviteWorkspaceMemberParams,
  UpdateWorkspaceMemberParams,
  RemoveWorkspaceMemberParams,
  UpdateWorkspaceInvitationParams,
} from "./workspace.types";

/**
 * Hook for workspace mutations
 * Based on RBAC v1 backend implementation
 */
export const useWorkspaceMutations = (
  workspaceId: string | number,
  axios: AxiosInstance,
  queryClient: QueryClient
) => {
  const normalizedWorkspaceId = normalizeId(workspaceId);

  // Invite workspace member mutation
  const inviteMember = useMutation({
    mutationFn: (params: InviteWorkspaceMemberParams) =>
      axios.post(USER_API.inviteWorkspaceMember(normalizedWorkspaceId), params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.members(normalizedWorkspaceId) });
      queryClient.invalidateQueries({ queryKey: workspaceKeys.invitations(normalizedWorkspaceId) });
      handleToast("Workspace member invited successfully!", "success");
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message || "Failed to invite workspace member. Please try again.";
      handleToast(message, "error");
    },
  });

  // Update workspace member mutation
  const updateMember = useMutation({
    mutationFn: (params: UpdateWorkspaceMemberParams) => {
      const payload: any = {
        role: params.role,
      };
      if (params.positionTitle !== undefined) {
        payload.position_title = params.positionTitle;
      }
      return axios.patch(
        USER_API.updateWorkspaceMember(normalizedWorkspaceId, params.memberId),
        payload
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.members(normalizedWorkspaceId) });
      queryClient.invalidateQueries({ queryKey: workspaceKeys.detail(normalizedWorkspaceId) });
      handleToast("Workspace member updated successfully!", "success");
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message || "Failed to update workspace member. Please try again.";
      handleToast(message, "error");
    },
  });

  // Remove workspace member mutation
  const removeMember = useMutation({
    mutationFn: (params: RemoveWorkspaceMemberParams) =>
      axios.delete(USER_API.removeWorkspaceMember(normalizedWorkspaceId, params.memberId), {
        data: { reason: params.reason },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.members(normalizedWorkspaceId) });
      queryClient.invalidateQueries({ queryKey: workspaceKeys.detail(normalizedWorkspaceId) });
      handleToast("Workspace member removed successfully!", "success");
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message || "Failed to remove workspace member. Please try again.";
      handleToast(message, "error");
    },
  });

  // Update workspace invitation mutation
  const updateInvitation = useMutation({
    mutationFn: (params: UpdateWorkspaceInvitationParams) =>
      axios.patch(USER_API.updateWorkspaceInvitation(normalizedWorkspaceId, params.invitationId), {
        role: params.role,
        message: params.message,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.invitations(normalizedWorkspaceId) });
      handleToast("Workspace invitation updated successfully!", "success");
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        "Failed to update workspace invitation. Please try again.";
      handleToast(message, "error");
    },
  });

  // Cancel workspace invitation mutation
  const cancelInvitation = useMutation({
    mutationFn: (invitationId: string) =>
      axios.delete(USER_API.cancelWorkspaceInvitation(normalizedWorkspaceId, invitationId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.invitations(normalizedWorkspaceId) });
      handleToast("Workspace invitation cancelled successfully!", "success");
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message ||
        "Failed to cancel workspace invitation. Please try again.";
      handleToast(message, "error");
    },
  });

  return {
    // Mutations
    inviteMember,
    updateMember,
    removeMember,
    updateInvitation,
    cancelInvitation,

    // Async versions
    inviteMemberAsync: inviteMember.mutateAsync,
    updateMemberAsync: updateMember.mutateAsync,
    removeMemberAsync: removeMember.mutateAsync,
    updateInvitationAsync: updateInvitation.mutateAsync,
    cancelInvitationAsync: cancelInvitation.mutateAsync,

    // Loading states
    isInviting: inviteMember.isPending,
    isUpdating: updateMember.isPending,
    isRemoving: removeMember.isPending,
    isUpdatingInvitation: updateInvitation.isPending,
    isCancellingInvitation: cancelInvitation.isPending,

    // Error states
    inviteError: inviteMember.error as Error | null,
    updateError: updateMember.error as Error | null,
    removeError: removeMember.error as Error | null,
    updateInvitationError: updateInvitation.error as Error | null,
    cancelInvitationError: cancelInvitation.error as Error | null,
  };
};

/**
 * Hook for workspace CRUD mutations
 */
export const useWorkspaceCrudMutations = (axios: AxiosInstance, queryClient: QueryClient) => {
  // Create workspace mutation
  const createWorkspace = useMutation({
    mutationFn: (params: { name: string; description?: string }) =>
      axios.post(USER_API.workspaces, params),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.all });
      handleToast("Workspace created successfully!", "success");
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message || "Failed to create workspace. Please try again.";
      handleToast(message, "error");
    },
  });

  // Update workspace mutation
  const updateWorkspace = useMutation({
    mutationFn: (params: { workspaceId: string; name?: string; description?: string }) =>
      axios.patch(USER_API.updateWorkspace(params.workspaceId), {
        name: params.name,
        description: params.description,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.all });
      queryClient.invalidateQueries({ queryKey: workspaceKeys.detail(variables.workspaceId) });
      handleToast("Workspace updated successfully!", "success");
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message || "Failed to update workspace. Please try again.";
      handleToast(message, "error");
    },
  });

  // Delete workspace mutation
  const deleteWorkspace = useMutation({
    mutationFn: (workspaceId: string) => axios.delete(USER_API.deleteWorkspace(workspaceId)),
    onSuccess: (_, workspaceId) => {
      queryClient.invalidateQueries({ queryKey: workspaceKeys.all });
      queryClient.invalidateQueries({ queryKey: workspaceKeys.detail(workspaceId) });
      handleToast("Workspace deleted successfully!", "success");
    },
    onError: (error: any) => {
      const message =
        error?.response?.data?.message || "Failed to delete workspace. Please try again.";
      handleToast(message, "error");
    },
  });

  return {
    // Mutations
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,

    // Async versions
    createWorkspaceAsync: createWorkspace.mutateAsync,
    updateWorkspaceAsync: updateWorkspace.mutateAsync,
    deleteWorkspaceAsync: deleteWorkspace.mutateAsync,

    // Loading states
    isCreating: createWorkspace.isPending,
    isUpdating: updateWorkspace.isPending,
    isDeleting: deleteWorkspace.isPending,

    // Error states
    createError: createWorkspace.error as Error | null,
    updateError: updateWorkspace.error as Error | null,
    deleteError: deleteWorkspace.error as Error | null,
  };
};
